import {Injectable} from "@angular/core";
import {ComponentStore} from "@ngrx/component-store";
import {firstValueFrom, forkJoin, lastValueFrom} from "rxjs";
import {filter, first} from "rxjs/operators";
import {AlertService} from "./alert.service";
import {ApiService} from "./api.service";
import {AuthService} from "./auth.service";
import {StorageService} from "./storage.service";
import {LoggingService} from "./logging.service";
import {NetworkService} from "./network.service";
import {NoticeInterface} from "../interfaces";

interface NotificationState {
  list: NoticeInterface[] | null;
}

@Injectable({
  providedIn: 'root',
})
export class NoticeService extends ComponentStore<NotificationState> {
  readonly list$ = this.select((state) => state.list);
  readonly setList = this.updater((state, list: NoticeInterface[] | null) => ({
    ...state,
    list,
  }));

  constructor(
    private readonly alert: AlertService,
    private readonly api: ApiService,
    private readonly auth: AuthService,
    private readonly logger: LoggingService,
    private readonly network: NetworkService,
    private readonly storage: StorageService,
  ) {
    super({
      list: null,
    });

    this.auth.isActive$.pipe(
      filter((active) => active),
      first(),
    ).subscribe(() => {
      this.load().then();
    });

    /* next version
      this.connection = new HubConnectionBuilder().withUrl("/api/data").build();
      this.connection.on("notice-posted", (notice) => {
        console.log("HUB - NOTICE: ", notice);
        this.notice.addNotice(notice);
      });
      this.connection.on("lastPunch-updated", (punch: PunchInterface) => {
        console.log("HUB - LAST PUNCH: ", punch);
        this.loadLastPunch([{ punches: [punch], closed: false }]);
      });

      // fire up websocket
      zip(this.ubwUser$, merge(of(this.network.getCurrentNetworkStatus()), this.network.onNetworkChange))
        .pipe(
          filter(([{ ubwUser }, networkStatus]) => ubwUser.subjectId && networkStatus === ConnectionStatus.Online),
          first(),
          tap(() => console.log("appMode: ", this.appMode)),
          filter(() => this.appMode === "app")
        )
        .subscribe(([{ ubwUser }]) => {
          this.initHub(ubwUser.authResno);
        });
    */
  }

  /* next version
    private initHub(authResno: string): void {
      console.log("HUB - INITIALIZING for: ", authResno);
      // const authResno = await this.authResno;
      this.connection.start().then(() => {
        console.log("HUB - STARTED for: ", authResno);
        this.connection.send("sub", `resno/${authResno}`).then(() => {
          // TODO:
        });
      });
    }
  */

  // add notice one-off, from device or websocket
  async add(notice: NoticeInterface): Promise<void> {
    const subjectId = this.auth.subjectId;
    const notices = await this.storage.getNotices(subjectId);
    // find and either update or add this notice
    const index = notices.findIndex((n) => notice.id !== 0 && n.id === notice.id);
    if (index >= 0) {
      notices[index] = notice;
    } else {
      notices.push(notice);
    }
    // coming from the device, id is 0 and needs to be sent to server
    // otherwise it's coming from server through websocket, id is set
    if (notice.id === 0) {
      await this.load(notices);
    } else {
      await this.storage.setNotices(subjectId, notices);
      this.setList(notices);
    }
  }

  async dismiss(id: number): Promise<void> {
    const subjectId = this.auth.subjectId;
    const notices = await this.storage.getNotices(subjectId);
    const index = notices.findIndex((n: NoticeInterface) => n.id === id);
    if (index >= 0) {
      notices[index].dismissed = true;
      notices[index].update = true;
      await this.load(notices);
    } else {
      await this.alert.present({
        header: 'Dismiss Notification',
        message: 'No notice found to dismiss. Please contact support.',
      });
    }
  }

  async load(notices?: NoticeInterface[]): Promise<void> {
    try {
      const subjectId = this.auth.subjectId;
      if (!notices) {
        notices = await this.storage.getNotices(subjectId);
      }
      if (this.network.isOnline$.value) {
        const noticesToPost = notices.filter((n: NoticeInterface) => n.id === 0 || n.update);
        if (noticesToPost.length > 0) {
          await lastValueFrom(
            forkJoin(
              noticesToPost.map((notice) =>
                this.api.postData<NoticeInterface, void>(`notices/${notice.id}`, {
                  ...notice,
                }),
              ),
            ),
          );
        }
        notices = (await firstValueFrom(
          this.api.getData<NoticeInterface[]>('notices'),
        )) as NoticeInterface[];
      }
      await this.storage.setNotices(subjectId, notices);
      this.setList(notices);
    } catch (e) {
      this.logger.error('loadNotices error: ', {loc: 'notice: 147', error: e});
      await this.alert.present({
        header: 'Notifications',
        message: `Oops, something went wrong getting your notifications. Please contacts support. Error: ${JSON.stringify(e)}`,
      });
    }
  }
}
